import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import selectIdToken from '../../redux/selectors/select-id-token'
import fileDownload from 'js-file-download'
import {
  AssignUserToCompanyAction,
  AssignUserToCompanyFailedAction,
  AssignUserToCompanySuccessAction,
  CreateCompanyAction,
  CreateCompanyFailedAction,
  CreateCompanySuccessAction,
  DeleteCompanyAction,
  DeleteCompanyFailedAction,
  DeleteCompanyIntegrationAction,
  DeleteCompanyIntegrationFailedAction,
  DeleteCompanyIntegrationSuccessAction,
  DeleteCompanySuccessAction,
  DeleteCompanyUserAction,
  DeleteCompanyUserFailedAction,
  DeleteCompanyUserSuccessAction,
  DownloadFileForAdminAction,
  DownloadFileWithinSpecificDatesForAdminAction,
  DownloadTransferFileForAdminAction,
  FetchCompaniesAction,
  FetchCompaniesFailAction,
  FetchCompaniesSuccessAction,
  FetchCompanyIntegrationAction,
  FetchCompanyIntegrationFailedAction,
  FetchCompanyIntegrationSuccessAction,
  FetchCompanyUsersAction,
  FetchCompanyUsersFailedAction,
  FetchCompanyUsersSuccessAction,
  FetchUnassignedUsersAction,
  FetchUnassignedUsersFailedAction,
  FetchUnassignedUsersSuccessAction,
  UpdateCompanyAction,
  UpdateCompanyFailedAction,
  UpdateCompanyIntegrationAction,
  UpdateCompanyIntegrationFailedAction,
  UpdateCompanyIntegrationSuccessAction,
  UpdateCompanySuccessAction
} from './admin-companies-actions'
import { AddMessageAction } from '../messages/messages-actions'
import { selectFromState } from '../selectors/select-from-state'
import {
  AdminCompaniesApi,
  type CompanyDeleteResponse,
  type CompanyIntegrationDeleteResponse,
  type CompanyIntegrationResponse,
  type CompanyUserAssignationResponse,
  type CompanyUserDeleteResponse,
  type FetchCompaniesResponse,
  type FetchCompanyUsersResponse,
  type FetchUnassignedUsersResponse,
  type UpdateCompanyIntegrationPayload,
  type UpdateCompanyRequestPayload,
  type UpdateCompanyResponse
} from '../../api/admin-companies-api'
import type { DateRange, DownloadFileResponse } from '../../api/types'
import { generateExportCsvFileName } from '../../utils/utils'
import { LogoutSuccessAction } from '../auth/auth-actions'
import { ApiUtil } from '../../api/api-utils'
import {
  type CreateCompanyActionPayload,
  type DeleteCompanyPayload,
  type DownloadFileForAdminPayload,
  type DownloadFileWithinSpecificDatesForAdminPayload,
  type DownloadTransferFileForAdminPayload,
  type UpdateCompanyActionPayload,
  type UpdateIntegrationPayload
} from './admin-companies-types'
import { MessageTypes } from '../messages/messages-types'

// Note: disabling requiring explicit return types for saga generators
// (as the type is pretty complex and produces little value as explicit)
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * assignUserToCompany(action: { payload: { companyId: string, userId: string } }) {
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: CompanyUserAssignationResponse =
        yield call(AdminCompaniesApi.assignUserToCompany, idToken, action.payload)

  if (response.succeeded) {
    const { id, companyId, username, systemAdmin } = response.payload.user
    yield all([
      put(AssignUserToCompanySuccessAction({ id, companyId, username, systemAdmin })),
      put(AddMessageAction('companies.assignCompanyUserSucceeded', MessageTypes.success))
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }
    yield all([
      put(AssignUserToCompanyFailedAction()),
      put(AddMessageAction(response.errorKey ?? 'companies.assignCompanyUserFailedDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * createCompany(action: { payload: CreateCompanyActionPayload }) {
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const requestPayload: UpdateCompanyRequestPayload = {
    ...action.payload
  }

  const response: UpdateCompanyResponse = yield call(AdminCompaniesApi.createCompany, idToken, requestPayload)

  if (response.succeeded) {
    yield all([
      put(CreateCompanySuccessAction(response.payload.company)),
      put(AddMessageAction('companies.createCompanySucceeded', MessageTypes.success))
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield all([
      put(CreateCompanyFailedAction()),
      put(AddMessageAction(response.errorKey ?? 'companies.createCompanyFailedDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * deleteCompany(action: { payload: DeleteCompanyPayload }) {
  const { companyId } = action.payload
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: CompanyDeleteResponse =
        yield call(AdminCompaniesApi.deleteCompany, idToken, companyId)

  if (response.succeeded) {
    yield all([
      put(DeleteCompanySuccessAction({ companyId })),
      put(AddMessageAction('companies.deleteCompanySucceeded', MessageTypes.success))
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield all([
      put(DeleteCompanyFailedAction()),
      put(AddMessageAction(response.errorKey ?? 'companies.deleteCompanyFailedDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * deleteCompanyUser(action: { payload: { companyId: string, userId: string } }) {
  const { companyId, userId } = action.payload
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const requestInput = {
    companyId,
    userId
  }

  const response: CompanyUserDeleteResponse =
        yield call(AdminCompaniesApi.deleteUser, idToken, requestInput)

  if (response.succeeded) {
    yield all([
      put(DeleteCompanyUserSuccessAction({ companyId, userId })),
      put(AddMessageAction('companies.deleteCompanyUserSucceeded', MessageTypes.success))
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield all([
      put(DeleteCompanyUserFailedAction()),
      put(AddMessageAction(response.errorKey ?? 'companies.deleteCompanyUserFailedDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * downloadFileWithinSpecificForAdminDates(action: {
  payload: DownloadFileWithinSpecificDatesForAdminPayload
}) {
  const companyId = action.payload.companyId
  const input: DateRange = {
    startDate: action.payload.startDateSelected,
    endDate: action.payload.endDateSelected
  }
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: DownloadFileResponse =
        yield call(AdminCompaniesApi.downloadMeterDataCsvWithinSpecificDates, idToken, companyId, input)

  if (response.succeeded) {
    const startDate = new Date(action.payload.startDateSelected)
    const endDate = new Date(action.payload.endDateSelected)

    fileDownload(response.payload.content, generateExportCsvFileName(startDate, endDate))
  } else {
    yield put(AddMessageAction(response.errorKey ?? 'companies.fileDownloadFailedDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * downloadFileForAdmin(action: { payload: DownloadFileForAdminPayload }) {
  const companyId = action.payload.companyId
  const input = {
    downloadType: action.payload.downloadType,
    startDate: action.payload.downloadDateSelected
  }

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const response: DownloadFileResponse =
        yield call(AdminCompaniesApi.downloadMeterDataCsvStartingFromDate, idToken, companyId, input)

  if (response.succeeded) {
    const date = new Date(action.payload.downloadDateSelected)
    const filename = `export${date.getFullYear()}${date.getMonth() + 1}${date.getDate()}.csv`
    fileDownload(response.payload.content, filename)
  } else {
    yield put(AddMessageAction(response.errorKey ?? 'companies.fileDownloadFailedDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * downloadTransferFileForAdmin(action: { payload: DownloadTransferFileForAdminPayload }) {
  const companyId: string = action.payload.companyId
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: DownloadFileResponse =
        yield call(AdminCompaniesApi.downloadLatestMeterDataTransferCsvFile, idToken, companyId)

  if (response.succeeded) {
    const date = new Date(Date.now())
    const month = parseInt(date.getMonth().toString(), 10) + 1
    const filename = 'export' + date.getFullYear() + '_' + month + '_' + date.getDate() + '.csv'
    fileDownload(response.payload.content, filename)
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }
    yield put(AddMessageAction(response.errorKey ?? 'companies.fileDownloadFailedDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * fetchCompaniesAsync() {
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const response: FetchCompaniesResponse = yield call(AdminCompaniesApi.fetchCompanies, idToken)
  if (response.succeeded) {
    const companies = response.payload.companies
    yield put(FetchCompaniesSuccessAction({
      companies
    }))
  } else {
    const errMsg = ApiUtil.failedResponseToDisplayString(response)
    console.warn('Error received on fetching companies (admin):', errMsg)
    yield put(FetchCompaniesFailAction())
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * fetchCompanyUsers(action: { payload: { companyId: string } }) {
  const { companyId } = action.payload
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: FetchCompanyUsersResponse = yield call(AdminCompaniesApi.fetchCompanyUsers, idToken, companyId)
  if (response.succeeded) {
    yield put(FetchCompanyUsersSuccessAction({
      companyId,
      users: response.payload.users
    }))
  } else {
    const errMsg = ApiUtil.failedResponseToDisplayString(response)
    console.warn('Error received on fetching company users (admin):', errMsg)
    yield put(FetchCompanyUsersFailedAction())
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * fetchUnassignedUsers() {
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const response: FetchUnassignedUsersResponse = yield call(AdminCompaniesApi.fetchUnassignedUsers, idToken)
  if (response.succeeded) {
    const unassignedUsers = response.payload.users
    yield put(FetchUnassignedUsersSuccessAction({
      users: unassignedUsers
    }))
  } else {
    const errMsg = ApiUtil.failedResponseToDisplayString(response)
    console.warn('Error received on fetching unassigned users (admin):', errMsg)
    yield put(FetchUnassignedUsersFailedAction())
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * updateCompany(action: { payload: UpdateCompanyActionPayload }) {
  const companyId: string = action.payload.id

  const requestPayload: UpdateCompanyRequestPayload = {
    name: action.payload.name,
    vatId: action.payload.vatId,
    streetAddress: action.payload.streetAddress,
    postalCode: action.payload.postalCode,
    town: action.payload.town
  }
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: UpdateCompanyResponse =
        yield call(AdminCompaniesApi.updateCompany, idToken, companyId, requestPayload)

  if (response.succeeded) {
    yield all([
      put(UpdateCompanySuccessAction(response.payload.company)),
      // Trigger refreshing company users (as the received API response payload do not include users)
      put(FetchCompanyUsersAction({ companyId: response.payload.company.id })),
      put(AddMessageAction('companies.updateSucceeded', MessageTypes.success))
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield all([
      put(UpdateCompanyFailedAction()),
      put(AddMessageAction(response.errorKey ?? 'companies.updateCompanyFailedDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * deleteCompanyIntegration(action: { payload: { companyId: string } }) {
  const companyId: string = action.payload.companyId

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  // Delete integration details
  const response: CompanyIntegrationDeleteResponse =
        yield call(AdminCompaniesApi.deleteCompanyIntegration, idToken, companyId)
  if (response.succeeded) {
    yield all([
      put(DeleteCompanyIntegrationSuccessAction({ companyId })),
      put(AddMessageAction('companyIntegrations.deleteSucceeded', MessageTypes.success))
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }
    yield all([
      put(DeleteCompanyIntegrationFailedAction()),
      put(AddMessageAction(
        response.errorKey ?? 'companyIntegrations.deleteCompanyIntegrationFailedDefaultError',
        MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * updateCompanyIntegration(action: { payload: UpdateIntegrationPayload }) {
  const { companyId } = action.payload

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  // Update or create integration details
  const payload: UpdateCompanyIntegrationPayload = {
    enabled: action.payload.enabled,
    integrationType: action.payload.integrationType,
    cronString: action.payload.cronString,
    url: action.payload.url,
    username: action.payload.username,
    password: action.payload.password,
    inPulseFormat: action.payload.inPulseFormat,
    tietoIdentifier: action.payload.tietoIdentifier
  }

  const response: CompanyIntegrationResponse =
        yield call(AdminCompaniesApi.updateCompanyIntegration, idToken, companyId, payload)

  if (response.succeeded) {
    yield all([
      put(UpdateCompanyIntegrationSuccessAction(response.payload.integration)),
      put(AddMessageAction('companyIntegrations.updateSucceeded', MessageTypes.success))
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield all([
      put(UpdateCompanyIntegrationFailedAction()),
      put(AddMessageAction(
        response.errorKey ?? 'companyIntegrations.updateCompanyIntegrationFailedDefaultError',
        MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * fetchCompanyIntegration(action: { payload: { companyId: string } }) {
  const companyId: string = action.payload.companyId

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: CompanyIntegrationResponse =
        yield call(AdminCompaniesApi.getCompanyIntegration, idToken, companyId)

  if (response.succeeded) {
    yield put(FetchCompanyIntegrationSuccessAction(response.payload.integration))
  } else {
    yield all([
      put(FetchCompanyIntegrationFailedAction()),
      put(AddMessageAction(
        response.errorKey ?? 'companyIntegrations.fetchCompanyIntegrationFailedDefaultError',
        MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function * adminCompaniesSagas() {
  // Actions that update a partial data set (e.g. array item in state)
  yield takeEvery(AssignUserToCompanyAction, assignUserToCompany)
  yield takeEvery(CreateCompanyAction, createCompany)
  yield takeEvery(DeleteCompanyAction, deleteCompany)
  yield takeEvery(DeleteCompanyIntegrationAction, deleteCompanyIntegration)
  yield takeEvery(DeleteCompanyUserAction, deleteCompanyUser)
  yield takeEvery(DownloadFileForAdminAction, downloadFileForAdmin)
  yield takeEvery(DownloadFileWithinSpecificDatesForAdminAction, downloadFileWithinSpecificForAdminDates)
  yield takeEvery(DownloadTransferFileForAdminAction, downloadTransferFileForAdmin)
  yield takeEvery(FetchCompanyIntegrationAction, fetchCompanyIntegration)
  yield takeEvery(UpdateCompanyAction, updateCompany)
  yield takeEvery(UpdateCompanyIntegrationAction, updateCompanyIntegration)
  // Actions that replace a full data set (e.g., full array in state)
  yield takeLatest(FetchCompaniesAction, fetchCompaniesAsync)
  yield takeLatest(FetchCompanyUsersAction, fetchCompanyUsers)
  yield takeLatest(FetchUnassignedUsersAction, fetchUnassignedUsers)
}
